import type { TokenResult } from "@bindery/auth-consumer/browser";
import type { Recommendation } from "@bindery/shared/reader/daily-reading";
import {
  READING_RECOMMENDATIONS_PATH,
  type ReadingRecommendationsRequest,
  readingRecommendationsErrorSchema,
  readingRecommendationsResponseSchema,
} from "@/contracts/reading-recommendations";

/**
 * `no_reader`: signed out, or a session with no reader selected; an expected
 * state that has no recommendations. `unavailable`: offline, or the route
 * failed; the caller falls back to the pool it last held.
 */
export type RecommendationsAnswer =
  | { kind: "pool"; candidates: Recommendation[] }
  | { kind: "no_reader" }
  | { kind: "unavailable" };

export interface RecommendationsApiDeps {
  fetch: (input: string, init: RequestInit) => Promise<Response>;
  fetchToken: () => Promise<TokenResult>;
}

async function isNotReader(response: Response): Promise<boolean> {
  try {
    const parsed = readingRecommendationsErrorSchema.safeParse(
      await response.json(),
    );
    return parsed.success && parsed.data.error === "not_reader";
  } catch {
    return false;
  }
}

export async function requestRecommendations(
  body: ReadingRecommendationsRequest,
  deps: RecommendationsApiDeps,
  signal?: AbortSignal,
): Promise<RecommendationsAnswer> {
  try {
    const token = await deps.fetchToken();
    if (token.kind === "signed-out") return { kind: "no_reader" };
    if (token.kind === "unreachable") return { kind: "unavailable" };
    const response = await deps.fetch(READING_RECOMMENDATIONS_PATH, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        authorization: `Bearer ${token.token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
      ...(signal && { signal }),
    });
    if (response.status === 401) return { kind: "no_reader" };
    if (response.status === 403 && (await isNotReader(response))) {
      return { kind: "no_reader" };
    }
    if (!response.ok) return { kind: "unavailable" };
    const parsed = readingRecommendationsResponseSchema.safeParse(
      await response.json(),
    );
    return parsed.success
      ? { kind: "pool", candidates: parsed.data.candidates }
      : { kind: "unavailable" };
  } catch {
    return { kind: "unavailable" };
  }
}
